import { useMemo } from "react";
import { PathPickProductFilter } from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilter";
import { PathPickProductRequestFilterKind } from "@/features/pathpick/types";
import { PathPickProductRequestParams } from "@/features/pathpick/types/request-params";
import {
  getPathPickAllRequestKinds,
  PATH_PICK_MARKET_PRICE_BASIS_PARAMS,
  PATH_PICK_NO_REQUEST_KINDS,
} from "@/features/pathpick/constants";
import useGetPathPickProducts from "@/features/pathpick/hooks/query/useGetPathPickProducts";
import usePathPickSyncKinds from "@/features/pathpick/hooks/usePathPickSyncKinds";
import { PathPickMarketSyncKind } from "@/features/pathpick/types";
import { toPathPickRangeFilterParams } from "@/features/pathpick/utils/range-filter";

interface UseGetPathPickSearchProductsParams {
  filter: PathPickProductFilter;
  pageNumber: number;
  pageSize: number;
}

const resolveCompletionFilter = (
  isCompletionTab: boolean,
  shouldExcludeCompleted: boolean
) => {
  if (isCompletionTab) {
    return true;
  }

  if (shouldExcludeCompleted) {
    return false;
  }

  return undefined;
};

// 주문 상품 제외 토글 — 주문 이력이 없는 상품만 본다.
const resolveOrderedFilter = (shouldExcludeOrdered: boolean) =>
  shouldExcludeOrdered ? false : undefined;

// 신청 이력 유무 — "처리 완료" 탭은 3종 중 하나라도 신청한 상품,
// 제외 토글은 신청 이력이 없는 상품만 본다(NONE 미배포 상태에서는 제외가 동작하지 않는다).
// syncKinds — 권한 있는 마켓(usePathPickSyncKinds). 네이버 외 마켓 연동만 한 상품도 처리 완료로 본다.
const resolveRequestKinds = (
  isProcessedTab: boolean,
  shouldExcludeProcessed: boolean,
  syncKinds: PathPickMarketSyncKind[]
): PathPickProductRequestFilterKind[] | undefined => {
  if (isProcessedTab) {
    return getPathPickAllRequestKinds(syncKinds);
  }

  if (shouldExcludeProcessed) {
    return PATH_PICK_NO_REQUEST_KINDS;
  }

  return undefined;
};

export const buildPathPickSearchProductsRequestParams = ({
  filter,
  pageNumber,
  pageSize, syncKinds,
}: UseGetPathPickSearchProductsParams & {
  syncKinds: PathPickMarketSyncKind[];
}): PathPickProductRequestParams => {
  const orderedStatus = resolveCompletionFilter(
    filter.tab === "ordered",
    Boolean(filter.excludeOrdered)
  );
  const marketPriceBasis = filter.marketPriceBasis ?? "bidPrice";

  return {
    brandId__in: filter.brandId__in,
    pathradeCategory__in: filter.pathradeCategory__in,
    query: filter.query,
    isBuyerOrdered: orderedStatus,
    requestType__in: resolveRequestKinds(
      filter.tab === "processed",
      Boolean(filter.excludeProcessed),
      syncKinds
    ),
    // 상품 유형(핫딜·KREAM 매칭·POIZON 매칭·즐겨찾기 브랜드)은 합집합으로 서버가 판정한다.
    // 즐겨찾기 브랜드도 서버가 바이어의 즐겨찾기를 직접 읽으므로 brandId__in에 싣지 않는다.
    collection__in: filter.collection__in,
    ...toPathPickRangeFilterParams(filter),
    kreamMatchedOnly: filter.tab === "kream" ? true : undefined,
    poizonMatchedOnly: filter.tab === "poizon" ? true : undefined,
    // 즐겨찾기 브랜드 탭은 전용 API 파라미터가 없어 즐겨찾기 브랜드 id를 위 brandId__in에 실어 보낸다
    // (주입은 useFavoriteBrandFilter가 담당하므로 여기서는 추가 매핑이 없다).
    orderBy: filter.orderBy,
    marketPriceBasis: PATH_PICK_MARKET_PRICE_BASIS_PARAMS[marketPriceBasis],
    productType: filter.tab === "hotDeal" ? "LINE_SHEET_PRODUCT" : undefined,
    pageNumber,
    pageSize,
  };
};

// 검색 화면의 UI 필터를 공용 상품 API의 요청 계약으로 변환하는 어댑터 훅이다.
const useGetPathPickSearchProducts = ({
  filter,
  pageNumber,
  pageSize,
}: UseGetPathPickSearchProductsParams) => {
  const syncKinds = usePathPickSyncKinds();
  const requestParams = useMemo(
    () =>
      buildPathPickSearchProductsRequestParams({
        filter,
        pageNumber,
        pageSize,
        syncKinds,
      }),
    [filter, pageNumber, pageSize, syncKinds]
  );

  return useGetPathPickProducts(requestParams);
};

export default useGetPathPickSearchProducts;
